import { Box, Typography, useTheme } from '@mui/material';
import { useFormikContext } from 'formik';
import React, { useCallback, useMemo } from 'react';
import { ColorResult, RGBColor } from 'react-color';
import { useSelector } from 'react-redux';

import {
  getInitialTemplateBuilderValues,
  GLOBAL_ACCENT_COLOR,
  GLOBAL_APP_BAR_BG_COLOR,
  GLOBAL_APP_BAR_LOGO_IMAGE,
  GLOBAL_APP_BAR_LOGO_SIZE,
  LOGO_SIZE_DEFAULT,
} from '../../../../../constants/templates';
import { useTemplateMediaUpload } from '../../../../../hooks/useTemplateImageUpload';
import { selectCurrentOrganizationLogoUrl } from '../../../../../store/selectors/organizations';
import { selectDefaultExperienceBlueprint } from '../../../../../store/selectors/shell';
import { IClaimLinkBuilderValues, UploadControlModeEnum } from '../../../../../types/templates';
import { FormLabel } from '../../../index';
import { AppLogoUploader, ColorPickerWithReset, LogoSizeControl } from '../../TemplateUploadControls';

interface ITitleProps {
  children: React.ReactNode;
}

const SectionTitle: React.FC<ITitleProps> = ({ children }) => {
  const theme = useTheme();
  const { typography } = theme;

  return (
    <Typography
      variant="body1"
      fontWeight={typography.fontWeightMedium}
      sx={{
        ml: -2,
      }}
    >
      {children}
    </Typography>
  );
};

export const BrandingStep: React.FC = React.memo(() => {
  const { values, setFieldValue } = useFormikContext<IClaimLinkBuilderValues>();
  const blueprint = useSelector(selectDefaultExperienceBlueprint);
  const organizationLogoUrl = useSelector(selectCurrentOrganizationLogoUrl);

  const { globalAccentColor, globalAppBarBackgroundColor, globalLogoImageUrl, globalLogoImageSize } = values;

  const [previewLogoImageSize, setPreviewLogoImageSize] = React.useState<number | null>(null);

  const effectiveLogoImageSize = useMemo(
    () => previewLogoImageSize ?? globalLogoImageSize,
    [previewLogoImageSize, globalLogoImageSize],
  );

  const blueprintDefaults = useMemo(
    () => getInitialTemplateBuilderValues(blueprint, organizationLogoUrl),
    [blueprint, organizationLogoUrl],
  );

  const handleMediaSuccess = useCallback(
    (fieldName: keyof IClaimLinkBuilderValues, value: string | null) => {
      setFieldValue(fieldName, value || '');
    },
    [setFieldValue],
  );

  const handleLogoRemove = useCallback(() => {
    setFieldValue(GLOBAL_APP_BAR_LOGO_IMAGE, organizationLogoUrl || '');
  }, [setFieldValue, organizationLogoUrl]);

  const { handleMediaUpload } = useTemplateMediaUpload({ onSuccess: handleMediaSuccess });

  const handleColorChange = useCallback(
    (fieldName: keyof IClaimLinkBuilderValues) => (color: ColorResult) => {
      setFieldValue(fieldName, color.rgb);
    },
    [setFieldValue],
  );

  const handleResetColor = useCallback(
    (fieldName: keyof IClaimLinkBuilderValues) => () => {
      setFieldValue(fieldName, blueprintDefaults[fieldName]);
    },
    [setFieldValue, blueprintDefaults],
  );

  const handleAppBarColorChange = useCallback(
    (color: RGBColor) => {
      const handler = handleColorChange(GLOBAL_APP_BAR_BG_COLOR);
      handler({ rgb: color } as ColorResult);
    },
    [handleColorChange],
  );

  const handleLogoSizeChange = useCallback(
    (percent: number) => {
      setFieldValue(GLOBAL_APP_BAR_LOGO_SIZE, percent);
    },
    [setFieldValue],
  );

  // Modal reset goes to the factory 100%, not the blueprint value (EX-6266)
  const handleLogoSizeReset = useCallback(() => {
    setFieldValue(GLOBAL_APP_BAR_LOGO_SIZE, LOGO_SIZE_DEFAULT);
  }, [setFieldValue]);

  const handleButtonColorChange = useCallback(
    (color: RGBColor) => {
      const handler = handleColorChange(GLOBAL_ACCENT_COLOR);
      handler({ rgb: color } as ColorResult);
    },
    [handleColorChange],
  );

  return (
    <>
      <Box>
        <SectionTitle>App bar</SectionTitle>
        <FormLabel label="Logo">
          <AppLogoUploader
            mode={UploadControlModeEnum.Global}
            globalValue={globalLogoImageUrl}
            defaultValue={blueprintDefaults.globalLogoImageUrl}
            onDrop={handleMediaUpload(GLOBAL_APP_BAR_LOGO_IMAGE)}
            onRemove={handleLogoRemove}
            logoSizePercent={effectiveLogoImageSize}
            dataAction="app_bar_logo"
          />
        </FormLabel>
      </Box>

      <Box sx={{ mt: 3 }}>
        <LogoSizeControl
          mode={UploadControlModeEnum.Global}
          globalValue={globalLogoImageSize}
          defaultValue={LOGO_SIZE_DEFAULT}
          onChange={handleLogoSizeChange}
          onReset={handleLogoSizeReset}
          onPreview={setPreviewLogoImageSize}
          dataAction="app_bar_logo_size"
        />
      </Box>

      <FormLabel label="Color" sx={{ mt: 3 }}>
        <ColorPickerWithReset
          mode={UploadControlModeEnum.Global}
          globalValue={globalAppBarBackgroundColor}
          defaultValue={blueprintDefaults.globalAppBarBackgroundColor}
          onChange={handleAppBarColorChange}
          onReset={handleResetColor(GLOBAL_APP_BAR_BG_COLOR)}
          dataAction="app_bar_color"
        />
      </FormLabel>

      <Box sx={{ mt: 4 }}>
        <SectionTitle>Theme</SectionTitle>
        <FormLabel label="Accent color">
          <ColorPickerWithReset
            mode={UploadControlModeEnum.Global}
            globalValue={globalAccentColor}
            defaultValue={blueprintDefaults.globalAccentColor}
            onChange={handleButtonColorChange}
            onReset={handleResetColor(GLOBAL_ACCENT_COLOR)}
            dataAction="accent_color"
          />
        </FormLabel>
      </Box>
    </>
  );
});
